import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import { pageMeta } from "@/lib/seo";
import { AlertTriangle, MapPin } from "lucide-react";
import { StatusChip } from "@/components/roava";
import { FamilyFollowMap } from "@/components/family/FamilyFollowMap";

export const Route = createFileRoute("/follow/$token")({
  head: () =>
    pageMeta({
      title: "Family Follow — Roava",
      description: "Read-only trip view shared by a Roava traveler with their family.",
      path: "/follow",
    }),
  component: FollowPage,
});

interface FeedResponse {
  paused?: boolean;
  tripName?: string;
  message?: string;
  traveler?: { displayName: string; avatarUrl: string | null };
  lastUpdatedAt?: string | null;
  lastReportedAt?: string | null;
  staleness?: "active" | "quiet" | "offline" | "unknown";
  delayMinutes?: number;
  privacy?: {
    sharingRegion: boolean;
    sharingExactLocation: boolean;
    sharingRoute: boolean;
    sharingHighlights: boolean;
    sharingAllPhotos: boolean;
    sharingSelectedPhotos?: boolean;
    photoMode?: "none" | "selected" | "highlights" | "all";
    regionPrecisionKm: number;
  };
  lastLocation?: { latitude: number; longitude: number; capturedAt: string } | null;
  route?: Array<{ latitude: number; longitude: number; capturedAt: string }>;
  photos?: Array<{ id: string; signedUrl: string; capturedAt: string | null; isHighlight: boolean }>;
  disclaimer?: string;
  error?: string;
}

function relTime(t: TFunction, iso: string | null | undefined) {
  if (!iso) return t("follow.rel_dash");
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.max(0, Math.round(diff / 60000));
  if (m < 1) return t("follow.rel_just_now");
  if (m < 60) return t("follow.rel_min", { n: m });
  const h = Math.round(m / 60);
  if (h < 24) return t("follow.rel_hour", { n: h });
  const d = Math.round(h / 24);
  return t("follow.rel_day", { n: d });
}

function fmtDelay(minutes: number) {
  return minutes >= 60 ? `${Math.round(minutes / 60)}h` : `${minutes}m`;
}

function FollowPage() {
  const { t } = useTranslation();
  const { token } = Route.useParams();
  const [state, setState] = useState<
    { status: "loading" } | { status: "ok"; data: FeedResponse } | { status: "error"; code: number; message: string }
  >({ status: "loading" });

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/family-follow-feed?token=${encodeURIComponent(
          token,
        )}`;
        const r = await fetch(url, {
          headers: {
            apikey: import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string,
          },
        });
        const body = (await r.json().catch(() => ({}))) as FeedResponse;
        if (cancelled) return;
        if (!r.ok) {
          setState({ status: "error", code: r.status, message: body.error ?? "Failed to load" });
          return;
        }
        setState({ status: "ok", data: body });
      } catch (e: any) {
        if (cancelled) return;
        setState({ status: "error", code: 0, message: e?.message ?? "Network error" });
      }
    }
    void load();
    const id = window.setInterval(load, 60_000);
    return () => {
      cancelled = true;
      window.clearInterval(id);
    };
  }, [token]);

  return (
    <div className="min-h-screen bg-[var(--roava-bg)]">
      <div
        className="mx-auto max-w-[640px] px-5 pb-12"
        style={{ paddingTop: "calc(env(safe-area-inset-top) + 1.5rem)" }}
      >
        <header className="flex items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-[11px] uppercase tracking-[0.14em] text-[var(--roava-text-muted)]">
              {t("follow.header_eyebrow")}
            </p>
            <h1 className="text-2xl font-bold mt-0.5 text-[var(--roava-text)]">
              {state.status === "ok" ? state.data.tripName ?? t("follow.trip_fallback") : t("follow.trip_fallback")}
            </h1>
          </div>
        </header>

        {state.status === "loading" && (
          <p className="text-sm text-[var(--roava-text-muted)]">{t("follow.loading")}</p>
        )}

        {state.status === "error" && <ErrorView code={state.code} message={state.message} />}

        {state.status === "ok" && <Feed data={state.data} />}

        <footer className="mt-10 text-center">
          <p className="text-[11px] text-[var(--roava-text-muted)]">
            {t("follow.footer")}
          </p>
        </footer>
      </div>
    </div>
  );
}

function ErrorView({ code, message }: { code: number; message: string }) {
  const { t } = useTranslation();
  const friendly =
    code === 404
      ? t("follow.err_404")
      : code === 410 && message === "expired"
      ? t("follow.err_expired")
      : code === 410 && message === "revoked"
      ? t("follow.err_revoked")
      : code === 423 && message === "trip_disabled"
      ? t("follow.err_trip_disabled")
      : code === 423 && message === "disabled_by_traveler"
      ? t("follow.err_disabled_by_traveler")
      : code === 429
      ? t("follow.err_429")
      : t("follow.err_unknown");
  return (
    <div className="rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)] p-6 text-center">
      <AlertTriangle className="size-8 text-[var(--roava-orange)] mx-auto" />
      <p className="mt-3 font-medium text-[var(--roava-text)]">{friendly}</p>
      <p className="mt-1 text-xs text-[var(--roava-text-muted)]">{t("follow.err_code", { code: message || code })}</p>
    </div>
  );
}

function Feed({ data }: { data: FeedResponse }) {
  const { t } = useTranslation();
  const staleness = data.staleness ?? "unknown";
  const reportedAt = data.lastReportedAt ?? data.lastUpdatedAt ?? null;
  const stalenessVariant =
    staleness === "active" ? "success" : staleness === "quiet" ? "warning" : staleness === "offline" ? "danger" : "neutral";
  const stalenessLabel =
    staleness === "active"
      ? t("follow.active_now")
      : staleness === "quiet"
      ? t("follow.quiet_last", { time: relTime(t, reportedAt) })
      : staleness === "offline"
      ? t("follow.offline_last", { time: relTime(t, reportedAt) })
      : t("follow.no_updates");

  const sharingItems = data.privacy
    ? [
        data.privacy.sharingExactLocation
          ? t("follow.share_exact")
          : data.privacy.sharingRegion
          ? t("follow.share_region", { km: data.privacy.regionPrecisionKm })
          : null,
        data.privacy.sharingRoute ? t("follow.share_route") : null,
        data.privacy.sharingHighlights ? t("follow.share_highlights") : null,
        data.privacy.sharingAllPhotos ? t("follow.share_all_photos") : null,
        data.privacy.sharingSelectedPhotos ? t("follow.share_selected_photos") : null,
      ]
        .filter(Boolean)
        .join(" · ")
    : "";

  return (
    <>
      {/* ALWAYS-ON safety disclaimer */}
      <div className="rounded-2xl border border-[var(--roava-orange)]/30 bg-[var(--roava-orange)]/10 p-3 mb-4 text-xs text-[var(--roava-text)]">
        <p className="font-semibold text-[var(--roava-orange)]">
          {t("follow.not_live_title")}
        </p>
        <p className="mt-0.5 text-[var(--roava-text-muted)]">
          {data.disclaimer ?? t("follow.default_disclaimer")}
        </p>
      </div>

      {data.paused ? (
        <div className="rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)] p-6 text-center">
          <p className="font-medium text-[var(--roava-text)]">
            {t("follow.paused_title", { trip: data.tripName ?? t("follow.trip_fallback") })}
          </p>
          <p className="mt-1 text-sm text-[var(--roava-text-muted)]">
            {data.message ?? t("follow.paused_default")}
          </p>
        </div>
      ) : (
        <>
          {/* Status header */}
          <div className="rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)] p-4 mb-4">
            <div className="flex items-center gap-3">
              {data.traveler?.avatarUrl ? (
                <img
                  src={data.traveler.avatarUrl}
                  alt=""
                  className="size-10 rounded-full object-cover"
                />
              ) : (
                <div className="size-10 rounded-full bg-[var(--roava-gray)] grid place-items-center text-sm font-semibold text-[var(--roava-text-muted)]">
                  {data.traveler?.displayName?.[0] ?? "·"}
                </div>
              )}
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold truncate">
                  {data.traveler?.displayName ?? t("follow.traveler_fallback")}
                </p>
                <div className="mt-1 flex items-center gap-2">
                  <StatusChip variant={stalenessVariant as any}>{stalenessLabel}</StatusChip>
                </div>
              </div>
            </div>
            {data.privacy && (
              <p className="mt-3 text-[11px] text-[var(--roava-text-muted)]">
                {t("follow.sharing_prefix", { items: sharingItems || t("follow.sharing_minimal") })}
                {typeof data.delayMinutes === "number" && data.delayMinutes > 0
                  ? t("follow.delay_suffix", { value: fmtDelay(data.delayMinutes) })
                  : ""}
              </p>
            )}
          </div>

          {/* Map */}
          {data.lastLocation && (
            <FamilyFollowMap
              lastLocation={{
                latitude: data.lastLocation.latitude,
                longitude: data.lastLocation.longitude,
              }}
              route={data.route?.map((p) => ({ latitude: p.latitude, longitude: p.longitude }))}
              exact={!!data.privacy?.sharingExactLocation}
              regionPrecisionKm={Number(data.privacy?.regionPrecisionKm ?? 10)}
            />
          )}

          {/* Region/location summary */}
          {data.lastLocation ? (
            <div className="rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)] p-4 mb-4">
              <div className="flex items-center gap-2">
                <MapPin className="size-4 text-[var(--roava-blue)]" />
                <p className="text-sm font-medium">
                  {data.privacy?.sharingExactLocation ? t("follow.last_position") : t("follow.last_region")}
                </p>
              </div>
              <p className="mt-2 font-mono text-xs text-[var(--roava-text-muted)]">
                {data.lastLocation.latitude.toFixed(3)}, {data.lastLocation.longitude.toFixed(3)}
              </p>
              <p className="text-[11px] text-[var(--roava-text-muted)] mt-1">
                {t("follow.as_of", { time: relTime(t, data.lastLocation.capturedAt) })}
                {typeof data.delayMinutes === "number" && data.delayMinutes > 0
                  ? t("follow.position_delay", { value: fmtDelay(data.delayMinutes) })
                  : ""}
              </p>
            </div>
          ) : (
            <div className="rounded-2xl border border-[var(--roava-border)] bg-[var(--roava-card)] p-4 mb-4 text-sm text-[var(--roava-text-muted)]">
              {t("follow.no_location")}
            </div>
          )}

          {/* Photos */}
          {data.photos && data.photos.length > 0 && (
            <div className="mb-4">
              <p className="text-sm font-medium mb-2">{t("follow.recent_highlights")}</p>
              <div className="grid grid-cols-3 gap-2">
                {data.photos.map((p) => (
                  <img
                    key={p.id}
                    src={p.signedUrl}
                    alt=""
                    loading="lazy"
                    className="aspect-square w-full rounded-xl object-cover bg-[var(--roava-gray)]"
                  />
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
}
